import React, { useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";

import { DataView, EmptyState, FilterDropdown, PurchaseConfirmDialog } from "/imports/ui/components/shared";
import { PackContinuationBanner } from "/imports/ui/components/booking";
import { plansContinuationCopy, formatContinuationDay } from "/imports/libs/packContinuation";
import usePackages from "./usePackages";
import { locationLabelFor } from "./packagesHelpers";
import VariantRow from "./components/VariantRow";
import MembershipRow from "./components/MembershipRow";
import LocationPicker from "./components/LocationPicker";

// On a phone the filters pair up two to a row, and an odd one out takes the whole row —
// left to wrap, it sat alone at a narrower width than the pair above it. On desktop
// they line up in one row, capped so they don't stretch the page.
const filterRowSx = {
  display: { xs: "grid", md: "flex" },
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  flexWrap: "wrap",
  gap: { xs: 1.5, md: 2 },
  "& > :last-child:nth-of-type(odd)": { gridColumn: "1 / -1" }
};
const filterFieldSx = { minWidth: 0, flex: { md: "1 1 9rem" }, maxWidth: { md: "17.5rem" } };

function PackagesView() {
  const {
    navigate,
    slug,
    currency,
    locations,
    filters,
    setFilter,
    loading,
    membershipsLoading,
    error,
    memberPricingFor,
    locById,
    buyingKey,
    expandedKey,
    setExpandedKey,
    confirm,
    wallets,
    setConfirm,
    visibleVariants,
    visibleMembershipRows,
    ownedForVariant,
    ownedMembershipFor,
    showLocationFilter,
    sportOptions,
    showSportFilter,
    categoryOptions,
    showCategoryFilter,
    nothingToShow,
    packageLocations,
    handleBuy,
    handleBuyMembership,
    openPackageConfirm,
    highlightKey,
    focusedVariant,
    setShowAllPlans,
    joinAfter,
    joinAfterClass,
    goToJoinAfter,
    backTo,
    goBackTo,
    continuations,
    continuing,
    continueSeries,
    stopContinuing
  } = usePackages();

  const showContinuations = !focusedVariant && !continuing && !joinAfter && !backTo && continuations.length > 0;

  const joinAfterWhen = joinAfterClass?.date
    ? `${DateTime.fromISO(joinAfterClass.date).toFormat("ccc d LLL")}${joinAfterClass.time ? ` · ${joinAfterClass.time}` : ""}`
    : null;

  // A client sent here from a class card lands on the pack that was advertised
  // to them, not at the top of a list they now have to search. Scrolling on
  // mount of the row itself, rather than on a loading flag, keeps it reliable —
  // packages and memberships settle on different ticks, so an effect can fire
  // while the row it wants is still absent.
  const scrolledKey = useRef(null);
  const attachHighlight = (node) => {
    if (!node || focusedVariant || scrolledKey.current === highlightKey) return;
    scrolledKey.current = highlightKey;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const renderVariant = ({ pkg, opt, key }) => {
    const isVariantBuying = buyingKey != null && (buyingKey === key || buyingKey.startsWith(`${key}@@`));
    return (
      <React.Fragment key={key}>
        <Box ref={key === highlightKey ? attachHighlight : null}>
          <VariantRow
            pkg={pkg}
            opt={opt}
            currency={currency || ""}
            onBuy={handleBuy}
            isBuying={isVariantBuying}
            memberPricing={memberPricingFor(pkg, opt)}
            locationLabel={locationLabelFor(pkg, locations, locById)}
            owned={ownedForVariant(pkg, opt)}
            highlighted={key === highlightKey}
          />
        </Box>
        {expandedKey === key && (
          <LocationPicker
            locations={packageLocations(pkg)}
            onClose={() => setExpandedKey(null)}
            onPick={(locId) => openPackageConfirm(pkg, opt, locId)}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <Box>
      <Box mb={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
          <IconButton size="small" onClick={() => navigate(`/${slug}`)} aria-label="Back to home" sx={{ ml: -0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            Packages
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          {focusedVariant
            ? "The pack that was offered on the session you picked."
            : "Buy a session pack to redeem at checkout, or a membership for ongoing access."}
        </Typography>
      </Box>

      {/* Sent here from a booking page: name what the pack is being bought for
          and keep the way back in sight. A class is a booking already picked; a
          slot search is not, so that one returns to the times instead. */}
      {(joinAfter || backTo) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 3,
            p: { xs: 1.5, md: 2 },
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06)
          }}
        >
          <EventAvailableOutlinedIcon sx={{ color: "primary.main", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: "10rem" }}>
            <Typography sx={{ fontWeight: 600, fontSize: { xs: "0.85rem", md: "0.9375rem" } }}>
              {joinAfter
                ? joinAfterClass?.name
                  ? `Buying a pack for ${joinAfterClass.name}`
                  : "Buying a pack for the class you picked"
                : "Buying a pack for the sessions you were booking"}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: { xs: "0.72rem", md: "0.8125rem" } }}>
              {joinAfter
                ? joinAfterWhen
                  ? `${joinAfterWhen} · redeem it at checkout`
                  : "Redeem it at checkout once it's paid for"
                : "Pick your time once it's paid for — redeem it at checkout"}
            </Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={joinAfter ? goToJoinAfter : goBackTo} sx={{ flexShrink: 0 }}>
            {joinAfter ? "Back to the class" : "Back to the times"}
          </Button>
        </Box>
      )}

      {showContinuations && (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {continuations.map((series) => {
            const copy = plansContinuationCopy(series);
            return (
              <PackContinuationBanner
                key={series.key}
                title={copy.title}
                subtitle={copy.subtitle}
                actionLabel="Continue"
                onAction={() => continueSeries(series)}
              />
            );
          })}
        </Stack>
      )}

      {!focusedVariant && !continuing && (showLocationFilter || showSportFilter || showCategoryFilter) && (
        <Box mb={3} sx={filterRowSx}>
          {showLocationFilter && (
            <Box sx={filterFieldSx}>
              <FilterDropdown
                label="Location"
                options={(locations || []).map((l) => ({ value: l._id, label: l.name }))}
                value={filters.locationId}
                onChange={(val) => setFilter("locationId", val)}
                allLabel="All Locations"
                fullWidth
              />
            </Box>
          )}

          {showSportFilter && (
            <Box sx={filterFieldSx}>
              <FilterDropdown
                label="Sport"
                options={sportOptions}
                value={filters.sport}
                onChange={(val) => setFilter("sport", val)}
                allLabel="All Sports"
                fullWidth
              />
            </Box>
          )}

          {showCategoryFilter && (
            <Box sx={filterFieldSx}>
              <FilterDropdown
                label="Type"
                options={categoryOptions}
                value={filters.categoryType}
                onChange={(val) => setFilter("categoryType", val)}
                allLabel="All Types"
                fullWidth
              />
            </Box>
          )}
        </Box>
      )}

      <DataView loading={loading || membershipsLoading} error={error} data={[1]} loadingMessage="Loading packages...">
        {nothingToShow ? (
          <EmptyState
            title="No packages available"
            description={
              filters.locationId
                ? "No packages are offered at this location — try another one."
                : "Check back later — this club hasn't published any packages or memberships yet."
            }
          />
        ) : focusedVariant ? (
          // The pack they were sent for, on its own. No section heading — the
          // banner above already says what it is being bought for — and a way
          // into the rest of the catalogue for anyone who wants to compare.
          <Box>
            <Stack spacing={2}>{renderVariant(focusedVariant)}</Stack>
            <Button variant="text" onClick={() => setShowAllPlans(true)} sx={{ mt: 2, textTransform: "none" }}>
              See all packages
            </Button>
          </Box>
        ) : continuing ? (
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Carry on from {formatContinuationDay(continuing.continueFrom)}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {continuing.variants.length === 1
                ? "The pack that continues your sessions."
                : "Any of these packs continues your sessions — pick the one you want."}
            </Typography>
            <Stack spacing={2}>{continuing.variants.map(renderVariant)}</Stack>
            <Button variant="text" onClick={stopContinuing} sx={{ mt: 2, textTransform: "none" }}>
              See all packages
            </Button>
          </Box>
        ) : (
          <Stack spacing={4}>
            {visibleVariants.length > 0 && (
              <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Packages
                </Typography>
                <Stack spacing={2}>{visibleVariants.map(renderVariant)}</Stack>
              </Box>
            )}

            {visibleMembershipRows.length > 0 && (
              <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Memberships
                </Typography>
                <Stack spacing={2}>
                  {visibleMembershipRows.map(({ membership, opt, key }) => (
                    <MembershipRow
                      key={key}
                      membership={membership}
                      opt={opt}
                      currency={currency || ""}
                      onBuy={handleBuyMembership}
                      isBuying={buyingKey === key}
                      owned={ownedMembershipFor(membership)}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DataView>

      <PurchaseConfirmDialog
        open={!!confirm}
        name={confirm?.name}
        lines={confirm?.lines || []}
        location={confirm?.location}
        price={confirm?.price}
        regularPrice={confirm?.regularPrice}
        currency={currency || ""}
        wallets={wallets}
        loading={buyingKey != null}
        onConfirm={async (choice) => {
          await confirm?.run(choice);
          setConfirm(null);
        }}
        onClose={() => setConfirm(null)}
      />
    </Box>
  );
}

export default PackagesView;
